import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ButtonComponent from "../../components/button";
import CoreAPI from "../../services/core";
import LoadingModal from "../../components/loading-modal";
import DropdownFilterComponent from "../../components/evaluation-form-components/dropdown-filter";
import SearchableField from "../../components/searchable-field";
import { axisClasses, BarChart, PieChart } from "@mui/x-charts";
import Tooltip from "@mui/material/Tooltip";
import Drawer from "@mui/material/Drawer";
import IconPlace from "../../assets/images/icon-location.svg";
import IconEvent from "../../assets/images/icon-calendar-1.svg";
import IconService from "../../assets/images/icon-report-1.svg";
import IconDelete from "../../assets/images/icon-fail.svg"
import { formatDateByString, onGroupBy, onSleep } from "../../utils/default";
import CalendarCustomComponent from "../calendar-custom";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

const Container = styled.div`
    width: 100%;
    margin-bottom: 16px;

    .filter-dashboard {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 36px;
    }

    .dashboard-box {
        display: flex;
        flex-direction: column;

        .label-header-info {
            color: #2A2A2A;
            font-size: 20px;
            font-weight: 500;
            line-height: 29.9px;
            text-align: left;

            .icon {
                width: 20px;
                margin-bottom: 2px;
                margin-left: 8px;
                cursor: pointer;
            }
        }

        .label-sub-header-info {
            color: #505050;
            font-size: 15px;
            font-weight: 400;
            line-height: 22.43px;
            text-align: left;
            margin-left: 44px;
        }

        .label-type-box {
            color: #2A2A2A;
            font-size: 15px;
            font-weight: 600;
            line-height: 22.43px;
            text-align: left;
        }

        .icon-type-box {
            width: 28px;
            min-width: 28px;
            height: 28px;
            border-radius: 50%;
            background-color: #E3E9FD;
            margin-right: 16px;
        }
    }

    .MuiBarLabel-root {
        &.MuiBarLabel-series-auto-generated-id-1 {
            fill: #FFFFFF !important;
        }
    }
`;

const FilterBox = styled.div`
    display: flex;
    gap: 16px;

    .line {
        width: 1px;
        height: 56px;
        margin-top: 22px;
        background-color: #F2F2F2;
    }
`;

const FilterTriggerButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 48px;
    padding: 0 18px;
    border-radius: 24px;
    border: 1px solid #DDDDDD;
    background: #FFFFFF;
    cursor: pointer;
    font-size: 14px;
    color: #505050;
    margin-top: 22px;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
        background: #FAFAFA;
        border-color: #B5B5B5;
    }

    &.has-active {
        border-color: #25368D;
        color: #25368D;
        background: #F2F5FE;
    }

    .filter-count-badge {
        background: #25368D;
        color: #FFFFFF;
        font-size: 11px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 10px;
        line-height: 1.2;
        min-width: 18px;
        text-align: center;
    }
`;

const FilterDrawerPanel = styled.div`
    width: 380px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    box-sizing: border-box;

    .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 12px;
        border-bottom: 1px solid #E5E5E5;
    }

    .drawer-title {
        font-size: 16px;
        font-weight: 600;
        color: #25368D;
    }

    .drawer-close-btn {
        background: none;
        border: none;
        font-size: 22px;
        cursor: pointer;
        color: #888888;
        line-height: 1;
        padding: 4px 8px;
        border-radius: 4px;

        &:hover {
            background: #F5F5F5;
        }
    }

    .drawer-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow-y: auto;
    }

    .drawer-footer {
        display: flex;
        gap: 12px;
        justify-content: space-between;
        align-items: center;
        padding-top: 12px;
        border-top: 1px solid #E5E5E5;
    }

    .drawer-clear-btn {
        background: none;
        border: 1px solid #DDDDDD;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        color: #C29200;
        font-size: 14px;

        &:hover {
            background: #FFF6D6;
            border-color: #C29200;
        }

        &:disabled {
            color: #BBBBBB;
            cursor: not-allowed;
            &:hover {
                background: none;
                border-color: #DDDDDD;
            }
        }
    }

    .drawer-apply-btn {
        background: #25368D;
        color: #FFFFFF;
        border: none;
        padding: 8px 20px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;

        &:hover {
            background: #1D2A6D;
        }
    }
`;

const TabBox = styled.div`
    display: flex;
    gap: 15px;
    margin-top: 16px;

    .tab-item {
        min-width: 136px;
        height: 40px;
        padding-left: 12px;
        padding-right: 12px;
        border-radius: 23px;
        background-color: #FFFFFF;
        border: 1px solid #B5B5B5;
        cursor: pointer;
        transition: all 0.2s ease;
        color: #505050;
        font-size: 15px;
        font-weight: 400;
        line-height: 22.43px;

        &.active {
            background-color: #F2F5FE;
            border: 1px solid #25368D;
            color: #25368D;
            font-size: 15px;
            font-weight: 500;
            line-height: 22.43px;
        }

        &:hover {
            background-color: #F2F5FE;
        }
    }
`;

const StatisticBox = styled.div`
    display: flex;
    width: 100%;
    height: 300px;

    .break-point {
        width: 6px;
        height: 6px;
        min-width: 6px;
        border-radius: 50%;
        margin-top: 2px;
        background-color: #25368D;
    }

    .chart-box {
        width: 70%;
        height: 100%;
        overflow-x: auto;
        flex-wrap: wrap;
    }

    .chart-sub-box {
        width: 100%;
        height: calc(100% - 16px);
        padding-top: 16px;

        .chart-title {
            padding-left: 8px;
            text-decoration: underline;
            cursor: pointer;
        }

        .chart {
            width: 100%;
            height: calc(100% - 48px);
        }
    }

    .comment-box {
        width: calc(30% - 16px);
        height: 100%;
        padding-left: 16px;

        .label-header {
            color: #25368D;
            font-size: 20px;
            font-weight: 500;
            line-height: 29.9px;
            text-align: left;
        }
    }

    .comment-sub-box {
        width: calc(100% - 16px);
        height: calc(100% - 16px - 32px);
        padding: 8px;
        background-color: #FFFFFF;
        border: 1px solid #B5B5B5;
        border-radius: 8px;
        overflow-y: auto;
    }
`;

const colorList = [
    "#1E90FF", "#28A745", "#FF6347", "#FFC107", "#17A2B8", "#6610F2", "#FF4500", "#20C997", "#6F42C1", "#007BFF",
    "#E83E8C", "#FD7E14", "#6C757D", "#00FF7F", "#FFD700", "#FF1493", "#8B0000", "#FF8C00", "#7CFC00", "#4682B4"
];
const chartSettingMain = {
    height: 300,
    sx: {},
    slotProps: {
        legend: { hidden: false },
        bar: {
            clipPath: "inset(0px round 8px 8px 0px 0px)",
        }
    },
    barLabel: "value"
};
const chartSettingA = {
    ...chartSettingMain,
};
const chartSettingB = {
    ...chartSettingMain,
};

function EFDashboardComponent({
    indexComponent,
    deleteable,
    userProfile,
    departments,
    onEnter,
    onRemove,
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [filterDetail, setFilterDetail] = useState({
        evaluation_type: null,
        department: null,
        division: null,
        province: null,
        location: null,
        evaluation_form: null,
        date: {
            type: "year",
            date: new Date(),
        },
    });
    const [listTypeEF, setListTypeEF] = useState([
        { id: 1, label: "Place" },
        { id: 2, label: "Event" },
        { id: 3, label: "Service" },
    ]);
    const [listDepartment, setListDepartment] = useState([]);
    const [listDivision, setListDivision] = useState([]);
    const [listProvince, setListProvince] = useState([]);
    const [listLocation, setListLocation] = useState([]);
    const [listEvaluationForm, setListEvaluationForm] = useState([]);
    const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
    const [dashboardDetail, setDashboardDetail] = useState({
        title: "ราชมังคลากีฬาสถาน",
        sub_title: "ราชมังคลากีฬาสถาน",
        type: "",
        date_label: "ปี 2567 (ม.ค. - เดือนปัจจุบัน)",
    });
    const [typeDashboard, setTypeDashboard] = useState(1);
    const [dataSetA, setDataSetA] = useState([
        // {
        //     service_amount: 1,
        //     survey_amount: 3,
        //     title: 'Jan',
        // },
        // {
        //     service_amount: 5,
        //     survey_amount: 12,
        //     title: 'Feb',
        // },
        // {
        //     service_amount: 3,
        //     survey_amount: 5,
        //     title: 'Mar',
        // },
        // {
        //     service_amount: 5,
        //     survey_amount: 7,
        //     title: 'Apr',
        // },
        // {
        //     service_amount: 8,
        //     survey_amount: 2,
        //     title: 'May',
        // },
        // {
        //     service_amount: 9,
        //     survey_amount: 4,
        //     title: 'Jun',
        // },
    ]);
    const [dataSetB, setDataSetB] = useState([
        // {
        //     satisfaction_amount: 2,
        //     expectation_amount: 2,
        //     title: 'Jan',
        // },
        // {
        //     satisfaction_amount: 1,
        //     expectation_amount: 3,
        //     title: 'Feb',
        // },
        // {
        //     satisfaction_amount: 5,
        //     expectation_amount: 3,
        //     title: 'Mar',
        // },
        // {
        //     satisfaction_amount: 2,
        //     expectation_amount: 1,
        //     title: 'Apr',
        // },
        // {
        //     satisfaction_amount: 3,
        //     expectation_amount: 2,
        //     title: 'May',
        // },
        // {
        //     satisfaction_amount: 1,
        //     expectation_amount: 4,
        //     title: 'Jun',
        // },
    ]);
    const [dataSetC, setDataSetC] = useState([]);
    const [seriesA, setSeriesA] = useState([
        {
            dataKey: 'service_amount',
            label: 'ใช้บริการ',
            color: "#FBE250",
        },
        {
            dataKey: 'survey_amount',
            label: 'ตอบแบบสอบถาม',
            color: "#25368D",
        },
    ]);
    const [seriesB, setSeriesB] = useState([
        {
            dataKey: 'expectation_amount',
            label: 'ระดับความคาดหวัง',
            color: "#FBE250",
        },
        {
            dataKey: 'satisfaction_amount',
            label: 'ระดับความพึงพอใจ',
            color: "#25368D",
        },
    ]);
    const [seriesC, setSeriesC] = useState([
        {
            dataKey: 'recommend_amount',
            label: 'แนะนำผู้อื่น',
            title: 'แนะนำผู้อื่น',
            color: "#FBE250",
        },
    ]);
    const [modeSatisExpectDashboard, setModeSatisExpectDashboard] = useState('');
    const [listPieStatistic, setListPieStatistic] = useState([]);
    const [listTextExpectation, setListTextExpectation] = useState([]);
    const [isEnter, setIsEnter] = useState(false);
    const chartStatisticRef = useRef(null);

    useEffect(() => {
        onInit();
    }, [])
    useEffect(() => {
        let list = [];
        if (Array.isArray(departments)) {
            list = [...departments]
        }
        setListDepartment(list)
        onQueryEF();
    }, [departments])
    // Auto-refresh dashboard when date changes, but only after the user has loaded stats once.
    useEffect(() => {
        if (isEnter && filterDetail.evaluation_form) {
            handleEnterSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDetail.date])

    async function onInit() {
        const responseProvince = await onGetAllLocation();
        setListProvince(responseProvince);
        // onSetPositionBarLabel(0);
    }
    async function onQueryEF() {
        const responseProvince = await onGetAllLocation();
        setListProvince(responseProvince);
        // ========== Always load full form list on mount (admin-scoped by backend) ==========
        const initialList = await onGetListEvaluationForm(null, null, null, null);
        setListEvaluationForm(initialList || []);
        // ========== Auto-expand accordion if a legacy cascade query param is present ==========
        const hasLegacyCascadeParam = ['department', 'division', 'province', 'location', 'evaluation_type']
            .some(k => searchParams.get(k));
        if (hasLegacyCascadeParam) {
            setIsAdvancedFiltersOpen(true);
        }
        // ========== ========== ========== ==========
        const evaluation_form_id = searchParams.get("evaluation_form_id");
        if (evaluation_form_id) {
            if (!departments || (Array.isArray(departments) && departments.length === 0)) return
            const ef = await onGetEFDetail(evaluation_form_id)
            let detail = {
                ...filterDetail,
                evaluation_type: null,
                department: null,
                division: null,
                province: null,
                location: null,
                evaluation_form: null,
            }
            if (ef) {
                detail.evaluation_form = {
                    "id": ef.evaluation_form_evaluation_form_id,
                    "label": ef.evaluation_form_evaluation_name,
                    "evaluation_name": ef.evaluation_form_evaluation_name,
                    "location_name": ef.location_location_name,
                }
            }
            const modelType = listTypeEF.find(x => x.label === ef?.evaluation_form_evaluation_type);
            detail.evaluation_type = modelType || null;
            const modelDepartment = departments.find(x => x.id === ef?.evaluation_form_department_id);
            detail.department = modelDepartment || null;
            if (detail.department) {
                let responseDivision = await onGetDivisionByDepertmentId([detail.department.id]);
                if (Array.isArray(responseDivision) && responseDivision.length > 0) {
                    const modelDivision = responseDivision.find(x => x.id === ef?.evaluation_form_division_id);
                    detail.division = modelDivision || null;
                    if (userProfile.division_id) {
                        responseDivision = responseDivision.filter(x => x.id === userProfile.division_id);
                    }
                    setListDivision(responseDivision)
                }
            }
            if (ef.evaluation_form_evaluation_type === "Place") {
                const modelProvince = responseProvince.find(x => x.id === ef?.location_province);
                detail.province = modelProvince || null;
                if (Array.isArray(modelProvince?.location)) {
                    const location = [...modelProvince.location]
                    const modelLocation = location.find(x => x.id === ef?.evaluation_form_location_id);
                    detail.location = modelLocation || null;
                    setListLocation(location || [])
                }
            }
            let response = await onGetListEvaluationForm(
                detail.evaluation_type.label,
                detail.department.id,
                detail.division.id,
                detail.location?.id,
            );
            setListEvaluationForm(response)

            setFilterDetail(detail)
            handleEnterSearch(detail);
        }
    }
    async function onGetEFDetail(evaluation_form_id) {
        let response = null;
        try {
            response = await CoreAPI.evaluationFormHttpService.evaluationFormById(evaluation_form_id)
        } catch { }
        return response
    }
    async function onGetDashboardService(evaluation_form_id) {
        let _dashboardDetail = {
            title: filterDetail.evaluation_form?.label || "",
            sub_title: filterDetail.evaluation_form?.location_name || "",
            type: filterDetail.evaluation_type?.label || null,
            date_label: "",
        }
        if (filterDetail.date.type === "year") {
            _dashboardDetail.date_label = filterDetail.date?.date ? `(ปี ${formatDateByString(filterDetail.date?.date, "YYYY")})` : "";
        } else if (filterDetail.date.type === "month") {
            _dashboardDetail.date_label = filterDetail.date?.date ? `(ปี ${formatDateByString(filterDetail.date?.date, "YYYY")} เดือน ${formatDateByString(filterDetail.date?.date, "MMMM")})` : "";
        }
        setDashboardDetail(_dashboardDetail)
        // ========== ========== ========== ==========
        let list = [];
        let response = null;
        try {
            let start_date = null;
            let end_date = null;
            if (filterDetail.date.type === "year") {
                start_date = dayjs(filterDetail.date.date).startOf('year')
                end_date = dayjs(filterDetail.date.date).endOf('year')
            } else if (filterDetail.date.type === "month") {
                start_date = dayjs(filterDetail.date.date).startOf('month')
                end_date = dayjs(filterDetail.date.date).endOf('month')
            }
            let body = {
                evaluation_form_id: evaluation_form_id,
                start_date: start_date.toDate(),
                end_date: end_date.toDate(),
                type: filterDetail.date.type,
            }
            response = await CoreAPI.satHttpService.getDashboardFrequencyEvaluatorServiceHistoryByParam(body);
        } catch { }
        if (response) {
            if (filterDetail.date.type === "year") {
                const listMonth = [
                    "ม.ค.",
                    "ก.พ.",
                    "มี.ค.",
                    "เม.ย.",
                    "พ.ค.",
                    "มิ.ย.",
                    "ก.ค.",
                    "ส.ค.",
                    "ก.ย.",
                    "ต.ค.",
                    "พ.ย.",
                    "ธ.ค.",
                ];
                for (let i = 0; i < 12; i++) {
                    let detail = {
                        service_amount: (i < response.service?.length) ? response.service[i].amount || 0 : 0,
                        survey_amount: (i < response.survey?.length) ? response.survey[i].amount || 0 : 0,
                        title: listMonth[i],
                    }
                    list.push(detail)
                }
            } else if (filterDetail.date.type === "month") {
                let date = new Date(filterDetail.date.date);
                let dayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                for (let i = 0; i < dayOfMonth; i++) {
                    let detail = {
                        service_amount: (i < response.service?.length) ? response.service[i].amount || 0 : 0,
                        survey_amount: (i < response.survey?.length) ? response.survey[i].amount || 0 : 0,
                        // title: `วันที่ ${i + 1}`,
                        title: `${i + 1}`,
                    }
                    list.push(detail)
                }
            }
        }
        // ========== ========== ========== ==========
        let service_amount = 0;
        let survey_amount = 0;
        for (let item of list) {
            service_amount += (item.service_amount || 0);
            survey_amount += (item.survey_amount || 0);
        }
        const _seriesA = [
            {
                dataKey: 'service_amount',
                label: `ใช้บริการ (${formatValue(service_amount)})`,
                color: "#FBE250",
            },
            {
                dataKey: 'survey_amount',
                label: `ตอบแบบสอบถาม (${formatValue(survey_amount)})`,
                color: "#25368D",
            },
        ]
        setSeriesA(_seriesA)
        // ========== ========== ========== ==========
        list = list.map(x => {
            let detail = { ...x }
            detail.service_amount = detail.service_amount ? Number(formatValue(detail.service_amount)) : 0;
            detail.survey_amount = detail.survey_amount ? Number(formatValue(detail.survey_amount)) : 0;
            return detail;
        })
        setDataSetA(list);
        return response;
    }
    async function onGetDashboardSatisfaction(evaluation_form_id) {
        let response = null;
        let mode = modeSatisExpectDashboard
        try {
            let start_date = null;
            let end_date = null;
            if (filterDetail.date.type === "year") {
                start_date = dayjs(filterDetail.date.date).startOf('year')
                end_date = dayjs(filterDetail.date.date).endOf('year')
            } else if (filterDetail.date.type === "month") {
                start_date = dayjs(filterDetail.date.date).startOf('month')
                end_date = dayjs(filterDetail.date.date).endOf('month')
            }
            let body = {
                evaluation_form_id: evaluation_form_id,
                start_date: start_date.toDate(),
                end_date: end_date.toDate(),
                type: filterDetail.date.type,
                mode: mode,
            }
            response = await CoreAPI.dashboardHttpService.getDashboardSatisExpecEvaluatorHistoryByParam(body);
        } catch { }
        if (response) {
            // ========== ========== ========== ==========
            let satisfaction_amount = 0;
            let expectation_amount = 0;
            let satisfaction_sum = 0;
            let expectation_sum = 0;
            for (let item of response) {
                satisfaction_amount += (item.satisfaction_amount || 0);
                expectation_amount += (item.expectation_amount || 0);
                satisfaction_sum += (item.satisfaction_rate || 0);
                expectation_sum += (item.expectation_rate || 0);
            }
            const _seriesB = [
                {
                    dataKey: 'expectation_amount',
                    label: mode === 'nps' ? 'ระดับความคาดหวัง' : `ระดับความคาดหวัง (${formatValue(expectation_amount)}/${expectation_sum})`,
                    color: "#FBE250",
                },
                {
                    dataKey: 'satisfaction_amount',
                    label: mode === 'nps' ? 'ระดับความพึงพอใจ' : `ระดับความพึงพอใจ (${formatValue(satisfaction_amount)}/${satisfaction_sum})`,
                    color: "#25368D",
                },
            ]
            setSeriesB(_seriesB)
            // ========== ========== ========== ==========
            response = response.map(x => {
                let detail = { ...x }
                detail.expectation_amount = detail.expectation_amount ? Number(formatValue(detail.expectation_amount)) : 0;
                detail.satisfaction_amount = detail.satisfaction_amount ? Number(formatValue(detail.satisfaction_amount)) : 0;
                return detail;
            })
            setDataSetB(response)
        }
        return response;
    }
    async function onGetDashboardStatistic(evaluation_form_id) {
        let response = null;
        try {
            let start_date = null;
            let end_date = null;
            if (filterDetail.date.type === "year") {
                start_date = dayjs(filterDetail.date.date).startOf('year')
                end_date = dayjs(filterDetail.date.date).endOf('year')
            } else if (filterDetail.date.type === "month") {
                start_date = dayjs(filterDetail.date.date).startOf('month')
                end_date = dayjs(filterDetail.date.date).endOf('month')
            }
            let body = {
                evaluation_form_id: evaluation_form_id,
                start_date: start_date.toDate(),
                end_date: end_date.toDate(),
                type: filterDetail.date.type,
            }
            response = await CoreAPI.dashboardHttpService.getDashboardStatisticEvaluatorHistoryByParam(body);
        } catch { }
        if (response) {
            // ========== ========== ========== ==========
            let statistic = [];
            if (Array.isArray(response.statistic)) {
                statistic = [...response.statistic];
                for (let item of statistic) {
                    if (Array.isArray(item.data) && item.data.length > 10) {
                        item.data = item.data.filter(x => x.value > 0)
                        console.log(item)
                    }
                }
            }
            setListPieStatistic(statistic)
            setListTextExpectation(response.expectation || [])
            // ========== ========== ========== ==========
            if (response.recommend) {
                let list = [];
                if (filterDetail.date.type === "year") {
                    const listMonth = [
                        "ม.ค.",
                        "ก.พ.",
                        "มี.ค.",
                        "เม.ย.",
                        "พ.ค.",
                        "มิ.ย.",
                        "ก.ค.",
                        "ส.ค.",
                        "ก.ย.",
                        "ต.ค.",
                        "พ.ย.",
                        "ธ.ค.",
                    ];
                    for (let i = 0; i < 12; i++) {
                        let detail = {
                            // recommend_amount: (i < response.recommend?.data?.length) ? response.recommend.data[i].avg || 0 : 0,
                            recommend_amount: (i < response.recommend?.data?.length) ? response.recommend.data[i].nps || 0 : 0,
                            title: listMonth[i],
                        }
                        list.push(detail)
                    }
                } else if (filterDetail.date.type === "month") {
                    let date = new Date(filterDetail.date.date);
                    let dayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                    for (let i = 0; i < dayOfMonth; i++) {
                        let detail = {
                            // recommend_amount: (i < response.recommend?.data?.length) ? response.recommend.data[i].avg || 0 : 0,
                            recommend_amount: (i < response.recommend?.data?.length) ? response.recommend.data[i].nps || 0 : 0,
                            // title: `วันที่ ${i + 1}`,
                            title: `${i + 1}`,
                        }
                        list.push(detail)
                    }
                }
                const _seriesC = [
                    {
                        dataKey: 'recommend_amount',
                        // label: `${response.recommend.title || "แนะนำผู้อื่น"}`,
                        label: `เฉลี่ย`,
                        title: response.recommend.title || "แนะนำผู้อื่น",
                        color: "#FBE250",
                    },
                ]
                setSeriesC(_seriesC)
                // ========== ========== ========== ==========
                list = list.map(x => {
                    let detail = { ...x }
                    detail.recommend_amount = detail.recommend_amount ? Number(formatValue(detail.recommend_amount)) : 0;
                    return detail;
                })
                setDataSetC(list);
            }
        }
        return response;
    }
    async function onGetDivisionByDepertmentId(list) {
        let response = [];
        try {
            const body = {
                list
            }
            response = await CoreAPI.userHttpService.userGetDivisionByDepertmentId(body)
        } catch { }
        if (response) {
            if (Array.isArray(response)) {
                response = response.map(x => {
                    const detail = {
                        id: x.division_id,
                        label: x.division_name,
                        department_id: x.department_id,
                    }
                    return detail
                })
            }
        }
        return response
    }
    async function onGetAllLocation() {
        let response = [];
        try {
            response = await CoreAPI.evaluationFormHttpService.evaluationFormGetListLocationGroupByProvince()
        } catch { }
        return response;
    }
    async function onGetListEvaluationForm(type, department_id, division_id, location_id) {
        let response = [];
        try {
            const body = {
                search: "",
                search_by: ["evaluation_name"],
                order: "ASC",
                order_by: "evaluation_name",
                limit: 999999,
                page: 1,
                type: type ? [type] : null,
                department: department_id ? [department_id] : null,
                division: division_id ? [division_id] : null,
                location: location_id ? [location_id] : null,
            };
            response = await CoreAPI.evaluationFormHttpService.evaluationFormGetListByParam(body)
        } catch { }
        if (response) {
            if (Array.isArray(response.data)) {
                response.data = response.data.map(x => {
                    const detail = {
                        id: x.evaluation_form_evaluation_form_id,
                        label: x.evaluation_form_evaluation_name,
                        evaluation_name: x.evaluation_form_evaluation_name,
                        location_name: x.location_location_name,
                    }
                    return detail
                })
            }
        }
        return response.data
    }

    function onReplaceTag(str) {
        str = str.replaceAll(' ', '\n');
        if (str.includes("ประชาสัมพันธ์")) {
            str = str.replaceAll('ประชาสัมพันธ์', 'ประชาสัมพันธ์\n');
            if (str.includes("การให้บริการ")) {
                str = str.replaceAll('การให้บริการ', 'การให้บริการ\n');

            }
            return str
        } else if (str.includes("เพียงพอ")) {
            return str.replaceAll('เพียงพอ', 'เพียงพอ\n');
        } else if (str.includes("ประสานงาน")) {
            str = str.replaceAll('ประสานงาน', '\nประสานงาน');
            if (str.includes("สะดวก")) {
                str = str.replaceAll('สะดวก', '\nสะดวก');
            }
            return str
        } else if (str.includes("เลือกปฏิบัติ")) {
            return str.replaceAll('เลือกปฏิบัติ', '\nเลือกปฏิบัติ');
        } else if (str.includes("ชัดเจน")) {
            return str.replaceAll('ชัดเจน', '\nชัดเจน');
        }
        return str;
    }
    async function onSetPositionBarLabel(param) {
        const count_max = 10;
        let checkChange = true;
        let elements = document.getElementsByClassName("MuiBarLabel-root");
        await onSleep(100);
        if (elements.length > 0) {
            for (let item of elements) {
                if (item.style?.height?.includes("px")) {
                    const height = Number(item.style?.height?.replace("px", ""))
                    const transform = item.style?.transform
                    const transformSplit = transform?.split(",")
                    if (Array.isArray(transformSplit) && transformSplit.length > 2) {
                        const height_old = transformSplit[1];
                        if (!isNaN(height)) {
                            const chart_height = chartSettingMain.height - 100;
                            let height_new = chart_height - height + 50;
                            if (chart_height - height === 0) {
                                height_new = height_new + 10;
                            } else {
                                height_new = height_new - 10;
                            }
                            const transform_new = item.style.transform.replace(`,${height_old},`, `, ${height_new}px,`);
                            // item.style.transform = transform_new;
                            if (transform !== transform_new) {
                                // checkChange = false;
                            }
                        }
                    }
                }
            }
        }
        if (!checkChange && param < count_max) {
            const count = param + 1;
            onSetPositionBarLabel(count)
        }
    }
    function formatValue(value) {
        const formattedValue = value.toFixed(2); // ทำให้เป็นทศนิยมสองตำแหน่ง

        // ถ้าลงท้ายด้วย ".00" ให้แสดงเฉพาะจำนวนเต็ม
        return formattedValue.endsWith(".00") ? parseInt(formattedValue) : formattedValue;
    }
    // ========== ========== ========== ==========
    async function handleEnterSearch(params) {
        let _filterDetail = { ...filterDetail };
        if (params) {
            _filterDetail = { ...params };
        }
        if (!_filterDetail.evaluation_form) return;
        setIsLoading(true);
        await onGetDashboardService(_filterDetail.evaluation_form?.id);
        await onGetDashboardSatisfaction(_filterDetail.evaluation_form?.id);
        await onGetDashboardStatistic(_filterDetail.evaluation_form?.id);
        setIsEnter(true)
        const returnData = {
            index: indexComponent,
            ef_id: _filterDetail.evaluation_form?.id,
        }
        onEnter && onEnter(returnData)
        // onSetPositionBarLabel(0);
        setIsLoading(false);
    }
    async function handleSelectFilter(key, value) {
        let rawValue = value?.length > 0 ? value[0] : null;
        let detail = {
            ...filterDetail
        }
        detail[key] = rawValue;
        if (key === "evaluation_type") {
            detail.evaluation_form = null;
            if (rawValue !== "Place") {
                detail.province = null;
                detail.location = null;
            }
        } else if (key === "department") {
            detail.evaluation_form = null;
            detail.division = null;
            if (rawValue) {
                let response = await onGetDivisionByDepertmentId([rawValue.id]);
                if (userProfile.division_id) {
                    response = response.filter(x => x.id === userProfile.division_id);
                }
                setListDivision(response)
            }
        } else if (key === "division") {
            detail.evaluation_form = null;
        } else if (key === "province") {
            detail.evaluation_form = null;
            detail.location = null;
            const responseLocation = listProvince.find(x => x.id === rawValue.id);
            setListLocation(responseLocation?.location || [])
        } else if (key === "location") {
            detail.evaluation_form = null;
        }
        // Re-fetch the form list whenever ANY cascade filter changes
        // (backend tolerates nulls and applies admin scope as fallback).
        if (key !== "evaluation_form") {
            const response = await onGetListEvaluationForm(
                detail.evaluation_type?.label || null,
                detail.department?.id || null,
                detail.division?.id || null,
                detail.location?.id || null,
            );
            setListEvaluationForm(response || []);
        }
        setFilterDetail(detail)
    }
    async function handleSelectDate(param) {
        let detail = {
            ...filterDetail
        }
        detail.date = { ...param }
        setFilterDetail(detail)
    }
    async function handleClearFilters() {
        const detail = {
            ...filterDetail,
            evaluation_type: null,
            department: null,
            division: null,
            province: null,
            location: null,
            // intentionally NOT clearing evaluation_form or date
        };
        setListDivision([]);
        setListLocation([]);
        setFilterDetail(detail);
        const response = await onGetListEvaluationForm(null, null, null, null);
        setListEvaluationForm(response || []);
    }
    function hasActiveCascadeFilter() {
        return Boolean(
            filterDetail.evaluation_type ||
            filterDetail.department ||
            filterDetail.division ||
            filterDetail.province ||
            filterDetail.location
        );
    }
    function getCascadeFilterCount() {
        return [
            filterDetail.evaluation_type,
            filterDetail.department,
            filterDetail.division,
            filterDetail.province,
            filterDetail.location,
        ].filter(Boolean).length;
    }
    async function handleRemove() {
        onRemove && onRemove()
    }
    async function handleSelectDashboardType(param) {
        setTypeDashboard(param)
        // onSetPositionBarLabel(0);
    }
    async function handleClickStatisticChartTitle(param) {
        const position = param + 1;
        chartStatisticRef.current.scrollTo({
            top: (300 * position) + (position ? 1 : 0),
            behavior: "smooth",
        });
    }

    return (
        <Container>
            <LoadingModal open={isLoading} />
            <div className="filter-dashboard">
                {/* ===== PRIMARY: form picker + date + filter trigger + submit ===== */}
                <FilterBox>
                    <div style={{ flex: 1, minWidth: "260px" }}>
                        <SearchableField
                            label="ค้นหาแบบสอบถาม"
                            placeholder="พิมพ์ชื่อแบบสอบถาม..."
                            options={listEvaluationForm}
                            value={filterDetail.evaluation_form || null}
                            required={true}
                            onChange={(picked) => handleSelectFilter("evaluation_form", picked ? [picked] : [])}
                        />
                    </div>
                    <CalendarCustomComponent
                        label={"ช่วงเวลา"}
                        placeholder={"เลือกช่วงเวลา"}
                        value={filterDetail.date}
                        mode={"single"}
                        styleContainer={{ width: "220px" }}
                        onSelect={handleSelectDate}
                    />
                    <FilterTriggerButton
                        type="button"
                        className={hasActiveCascadeFilter() ? 'has-active' : ''}
                        onClick={() => setIsAdvancedFiltersOpen(true)}
                    >
                        <span>ตัวกรอง</span>
                        {getCascadeFilterCount() > 0 && (
                            <span className="filter-count-badge">{getCascadeFilterCount()}</span>
                        )}
                    </FilterTriggerButton>
                    <div className="vertical-center" style={{ paddingTop: filterDetail.evaluation_form ? '20px' : '24px' }}>
                        <ButtonComponent
                            label={"ดูสถิติ"}
                            variant={"secondary"}
                            sceenSize={"lg"}
                            disable={filterDetail.evaluation_form ? false : true}
                            style={{ width: "156px", maxWidth: "156px", minWidth: "156px" }}
                            onClick={handleEnterSearch}
                        />
                    </div>
                </FilterBox>
                {/* ===== Right-side cascade filter drawer ===== */}
                <Drawer
                    anchor="right"
                    open={isAdvancedFiltersOpen}
                    onClose={() => setIsAdvancedFiltersOpen(false)}
                >
                    <FilterDrawerPanel>
                        <div className="drawer-header">
                            <div className="drawer-title">ตัวกรอง</div>
                            <button
                                type="button"
                                className="drawer-close-btn"
                                onClick={() => setIsAdvancedFiltersOpen(false)}
                                aria-label="ปิด"
                            >
                                ×
                            </button>
                        </div>
                        <div className="drawer-body">
                            <SearchableField
                                label="ประเภทแบบสอบถาม"
                                placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                                options={listTypeEF}
                                value={filterDetail.evaluation_type || null}
                                onChange={(picked) => handleSelectFilter("evaluation_type", picked ? [picked] : [])}
                            />
                            <SearchableField
                                label="ฝ่าย"
                                placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                                options={listDepartment}
                                value={filterDetail.department || null}
                                onChange={(picked) => handleSelectFilter("department", picked ? [picked] : [])}
                            />
                            <SearchableField
                                label="กอง"
                                placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                                options={listDivision}
                                value={filterDetail.division || null}
                                onChange={(picked) => handleSelectFilter("division", picked ? [picked] : [])}
                            />
                            {filterDetail.evaluation_type?.id === 1 && (
                                <>
                                    <SearchableField
                                        label="จังหวัด"
                                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                                        options={listProvince}
                                        value={filterDetail.province || null}
                                        onChange={(picked) => handleSelectFilter("province", picked ? [picked] : [])}
                                    />
                                    <SearchableField
                                        label="สถานที่"
                                        placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                                        options={listLocation}
                                        value={filterDetail.location || null}
                                        onChange={(picked) => handleSelectFilter("location", picked ? [picked] : [])}
                                    />
                                </>
                            )}
                        </div>
                        <div className="drawer-footer">
                            <button
                                type="button"
                                className="drawer-clear-btn"
                                onClick={handleClearFilters}
                                disabled={!hasActiveCascadeFilter()}
                            >
                                ล้างตัวกรอง
                            </button>
                            <button
                                type="button"
                                className="drawer-apply-btn"
                                onClick={() => setIsAdvancedFiltersOpen(false)}
                            >
                                ปิด
                            </button>
                        </div>
                    </FilterDrawerPanel>
                </Drawer>
            </div>
            {isEnter &&
                <div className="dashboard-box">
                    <div className="label-header-info vertical-center">
                        <div className="content-center icon-type-box">
                            <img alt="img" src={dashboardDetail.title === "Place" ? IconPlace : dashboardDetail.title === "Event" ? IconEvent : IconService}
                                style={{ width: "16px" }} />
                        </div>
                        {dashboardDetail.title} {dashboardDetail.date_label}
                        {deleteable &&
                            <img className="icon" alt="img" src={IconDelete} onClick={handleRemove} />
                        }
                    </div>
                    {dashboardDetail.sub_title &&
                        <div className="label-sub-header-info vertical-center">
                            {dashboardDetail.sub_title}
                        </div>
                    }
                    <TabBox>
                        <div className={`content-center tab-item ${typeDashboard === 1 ? "active" : ""}`}
                            onClick={(e) => handleSelectDashboardType(1)}>
                            ด้านการใช้บริการ
                        </div>
                        <div className={`content-center tab-item ${typeDashboard === 2 ? "active" : ""}`}
                            onClick={(e) => handleSelectDashboardType(2)}>
                            ด้านความพึงพอใจ {modeSatisExpectDashboard === 'nps' ? '(NPS)' : ''}
                        </div>
                        <div className={`content-center tab-item ${typeDashboard === 3 ? "active" : ""}`}
                            onClick={(e) => handleSelectDashboardType(3)}>
                            สถิติ
                        </div>
                    </TabBox>
                    {typeDashboard === 1 &&
                        <BarChart
                            dataset={dataSetA}
                            series={seriesA}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    dataKey: 'title',
                                    tickPlacement: 'start',
                                    tickLabelPlacement: 'middle',
                                    // tickLabelStyle: {
                                    //     angle: 45,
                                    //     textAnchor: 'start',
                                    //     fontSize: 12,
                                    // },
                                },
                            ]}
                            yAxis={[{
                                valueFormatter: (value) => `${value} ครั้ง`,
                            }]}
                            {...chartSettingA}
                        />
                    }
                    {typeDashboard === 2 &&
                        <BarChart
                            dataset={dataSetB}
                            series={seriesB}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    dataKey: 'title',
                                    tickPlacement: 'start',
                                    tickLabelPlacement: 'middle',
                                    valueFormatter: (value) => {
                                        return onReplaceTag(value);
                                    },
                                    // tickLabelStyle: {
                                    //     angle: 25,
                                    //     textAnchor: 'start',
                                    //     fontSize: 12,
                                    // },
                                },
                            ]}
                            yAxis={[{
                                valueFormatter: (value) => `${value}`,
                                max: modeSatisExpectDashboard === 'nps' ? 100 : 10,
                                min: modeSatisExpectDashboard === 'nps' ? -100 : 0,
                            }]}
                            {...chartSettingB}
                        />
                    }
                    {typeDashboard === 3 &&
                        <StatisticBox>
                            <div ref={chartStatisticRef} className="chart-box vertical-center">
                                <div className="chart-sub-box">
                                    <div className="vertical-center" style={{ width: "100%", height: "40px" }}>
                                        <div className="break-point"></div>
                                        <div className="chart-title"
                                            onClick={(e) => { handleClickStatisticChartTitle(-1) }}>
                                            {seriesC.length > 0 ? seriesC[0].title : "แนะนำผู้อื่น"}
                                        </div>
                                    </div>
                                    <div className="chart">
                                        <BarChart
                                            dataset={dataSetC}
                                            series={seriesC}
                                            xAxis={[
                                                {
                                                    scaleType: 'band',
                                                    dataKey: 'title',
                                                    tickPlacement: 'start',
                                                    tickLabelPlacement: 'middle',
                                                },
                                            ]}
                                            yAxis={[{
                                                valueFormatter: (value) => `${value}`,
                                                max: 100,
                                                min: -100,
                                            }]}
                                            // height={300}
                                            slotProps={{
                                                legend: { hidden: true },
                                                bar: {
                                                    clipPath: "inset(0px round 8px 8px 0px 0px)",
                                                }
                                            }}
                                            barLabel={"value"}
                                        />
                                    </div>
                                    <div style={{ width: "100%", height: "1px", marginTop: "8px", backgroundColor: "#B5B5B5" }}></div>
                                </div>
                                {listPieStatistic.map((value, index) => {
                                    return (
                                        <div key={index} className="chart-sub-box">
                                            <div className="vertical-center" style={{ width: "100%", height: "40px" }}>
                                                <div className="break-point"></div>
                                                <div className="chart-title"
                                                    onClick={(e) => { handleClickStatisticChartTitle(index) }}>
                                                    {value.title}
                                                </div>
                                            </div>
                                            <div className="chart">
                                                <PieChart
                                                    colors={colorList}
                                                    series={[
                                                        {
                                                            arcLabel: (item) => `${item.value || ""}`,
                                                            data: value.data,
                                                        },
                                                    ]}
                                                    margin={{ bottom: 80 }}
                                                    slotProps={{
                                                        legend: {
                                                            direction: 'row',
                                                            position: { vertical: 'bottom', horizontal: 'middle' },
                                                            padding: 0,
                                                        },
                                                    }}
                                                />
                                            </div>
                                            <div style={{ width: "100%", height: "1px", marginTop: "8px", backgroundColor: "#B5B5B5" }}></div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="comment-box">
                                <div className="label-header">ข้อเสนอแนะ</div>
                                <div className="comment-sub-box">
                                    {listTextExpectation.map((value, index) => {
                                        const isObj = value && typeof value === 'object';
                                        const text = isObj ? value.text : value;
                                        const count = isObj ? (value.count || 1) : 1;
                                        const evaluators = isObj && Array.isArray(value.evaluators) ? value.evaluators : [];
                                        const tooltipTitle = evaluators
                                            .map(e => e?.name)
                                            .filter(Boolean)
                                            .join(', ');
                                        return (
                                            <Tooltip
                                                key={index}
                                                title={tooltipTitle || ''}
                                                arrow
                                                placement="top"
                                                disableHoverListener={!tooltipTitle}
                                            >
                                                <div style={{ width: "100%", marginBottom: "8px", cursor: tooltipTitle ? 'help' : 'default' }}>
                                                    <div className="vertical-center" style={{ wordBreak: "break-word" }}>
                                                        <div className="break-point"></div>
                                                        <div style={{ paddingLeft: "8px", whiteSpace: "pre-line", display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                                            <span style={{ flex: 1 }}>{text}</span>
                                                            {count > 1 && (
                                                                <span
                                                                    style={{
                                                                        flexShrink: 0,
                                                                        fontSize: '12px',
                                                                        fontWeight: 600,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '10px',
                                                                        backgroundColor: '#FFEDB3',
                                                                        color: '#7A5A00',
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    ×{count}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{ width: "100%", height: "1px", marginTop: "8px", backgroundColor: "#B5B5B5" }}></div>
                                                </div>
                                            </Tooltip>
                                        )
                                    })}
                                </div>
                            </div>
                        </StatisticBox>
                    }
                </div>
            }
        </Container>
    )
}

export default EFDashboardComponent;